import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';

// 🔥 IMPORT PRO SOUND ENGINE
import { playSound } from './Board/BoardConstants';

export const PIECES = {
    'Rook': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wR.svg',
    'Bishop': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wB.svg',
    'Queen': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wQ.svg',
    'King': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wK.svg',
    'Knight': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wN.svg',
    'Pawn': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wP.svg'
};

export const HUNGRY_CHAPTERS = [
    {
        id: 'Rook',
        piece: 'Rook',
        hint: "Rooks only move straight like a plus sign + !",
        levels: [
            { id: 1, start: 'e4', apples: ['e7', 'h7'] },
            { id: 2, start: 'a1', apples: ['a5', 'g5', 'g2'] },
            { id: 3, start: 'd4', apples: ['d7', 'a7', 'a4', 'h4', 'h2', 'd2'] }
        ]
    },
    {
        id: 'Bishop',
        piece: 'Bishop',
        hint: "Bishops only move diagonally like an X !",
        levels: [
            { id: 1, start: 'c2', apples: ['f5', 'd7'] },
            { id: 2, start: 'a1', apples: ['d4', 'a7', 'f2'] },
            { id: 3, start: 'd4', apples: ['g7', 'h8', 'a1', 'g1'] }
        ]
    },
    {
        id: 'Queen',
        piece: 'Queen',
        hint: "Queens can move straight OR diagonally!",
        levels: [
            { id: 1, start: 'd4', apples: ['d7', 'a7', 'a4', 'g4'] },
            { id: 2, start: 'e5', apples: ['b8', 'b2', 'h2', 'e2'] },
            { id: 3, start: 'c3', apples: ['c8', 'h8', 'h3', 'd3', 'd1'] }
        ]
    },
    {
        id: 'King',
        piece: 'King',
        hint: "Kings move one step in any direction!",
        levels: [
            { id: 1, start: 'e4', apples: ['e5', 'd6'] },
            { id: 2, start: 'c3', apples: ['d3', 'd2', 'e1'] },
            { id: 3, start: 'e4', apples: ['e5', 'f6', 'g6', 'g5', 'f4'] }
        ]
    },
    {
        id: 'Knight',
        piece: 'Knight',
        hint: "Knights jump in an L-shape!",
        levels: [
            { id: 1, start: 'd4', apples: ['e6', 'g5'] },
            { id: 2, start: 'c3', apples: ['d5', 'f6', 'e4'] },
            { id: 3, start: 'd4', apples: ['e6', 'f4', 'e2', 'c3', 'b5', 'c7'] }
        ]
    },
    {
        id: 'Pawn',
        piece: 'Pawn',
        hint: "Pawns move straight (1 block) but capture diagonally!",
        levels: [
            { id: 1, start: 'e2', apples: ['e3', 'e4'] },
            { id: 2, start: 'c2', apples: ['d3', 'd4', 'e5'] },
            { id: 3, start: 'a2', apples: ['b3', 'b4', 'c5', 'c6', 'd7'] }
        ]
    }
];

const APPLE_IMG = '🍎';
const ROWS = [8, 7, 6, 5, 4, 3, 2, 1];
const COLS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

export default function HungryPieceGame({ db, classroomId, userEmail, initialChapter = 'Rook', onWin, onChapterComplete }) {
    
    // Find chapter based on initialChapter string, default to Rook if not found
    const chapterIdx = HUNGRY_CHAPTERS.findIndex(c => c.id === initialChapter);
    const safeChapterIdx = Math.max(0, chapterIdx);
    
    const [currentChapterIdx, setCurrentChapterIdx] = useState(safeChapterIdx);
    const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
    
    const chapter = HUNGRY_CHAPTERS[currentChapterIdx];
    const level = chapter.levels[currentLevelIdx];
    
    const [piecePos, setPiecePos] = useState(level.start);
    const [apples, setApples] = useState(level.apples);
    
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [totalChapterMistakes, setTotalChapterMistakes] = useState(0);
    const [showWinModal, setShowWinModal] = useState(false);

    useEffect(() => {
        const idx = HUNGRY_CHAPTERS.findIndex(c => c.id === initialChapter);
        if (idx !== -1) {
            setCurrentChapterIdx(idx);
            setCurrentLevelIdx(0);
            setPiecePos(HUNGRY_CHAPTERS[idx].levels[0].start);
            setApples(HUNGRY_CHAPTERS[idx].levels[0].apples);
            setWrongAttempts(0);
        }
    }, [initialChapter]);

    // Dynamic Coordinate calculation
    const getSquareCoords = (sq) => {
        const file = COLS.indexOf(sq[0]);
        const rank = ROWS.indexOf(parseInt(sq[1]));
        return { 
            left: `${(file / 8) * 100}%`, 
            top: `${(rank / 8) * 100}%` 
        };
    };

    const isMoveLegal = (f, t, pieceType) => {
        const fFile = f.charCodeAt(0) - 97; const fRank = parseInt(f[1]);
        const tFile = t.charCodeAt(0) - 97; const tRank = parseInt(t[1]);
        const dx = Math.abs(fFile - tFile);
        const dy = Math.abs(fRank - tRank);

        if (pieceType === 'Rook') return dx === 0 || dy === 0;
        if (pieceType === 'Bishop') return dx === dy;
        if (pieceType === 'Queen') return dx === 0 || dy === 0 || dx === dy;
        if (pieceType === 'King') return dx <= 1 && dy <= 1 && (dx > 0 || dy > 0);
        if (pieceType === 'Knight') return (dx === 1 && dy === 2) || (dx === 2 && dy === 1);
        if (pieceType === 'Pawn') {
            // Forward by 1 or capture diagonally by 1
            const forwardRank = fRank + 1;
            if (tRank !== forwardRank) return false;
            if (dx === 0) return !apples.includes(t); // Cant capture straight
            if (dx === 1) return apples.includes(t); // Only diagonal if apple there
            return false;
        }
        return false;
    };

    const calculateLegalMoves = (pos, pieceType) => {
        const moves = [];
        ROWS.forEach(r => COLS.forEach(c => {
            const sq = `${c}${r}`;
            if (sq !== pos && isMoveLegal(pos, sq, pieceType)) moves.push(sq);
        }));
        return moves;
    };

    // Firebase live update for classroom monitor
    useEffect(() => {
        if (db && classroomId && userEmail) {
            const safeEmail = userEmail.replace(/\./g, '_');
            updateDoc(doc(db, "classes", classroomId), {
                [`miniGameProgress.${safeEmail}`]: {
                    chapter: chapter.id,
                    level: level.id,
                    piece: chapter.piece,
                    applesLeft: apples.length,
                    apples: apples,
                    piecePos: piecePos, 
                    wrongAttempts: wrongAttempts
                }
            }).catch(() => {});
        }
    }, [apples, piecePos, chapter.id, level.id, wrongAttempts, db, classroomId, userEmail]);

    useEffect(() => {
        const c = HUNGRY_CHAPTERS[currentChapterIdx];
        const l = c.levels[currentLevelIdx];
        setPiecePos(l.start);
        setApples(l.apples);
        setSelectedSquare(null);
        setWrongAttempts(0);
    }, [currentChapterIdx, currentLevelIdx]);

    const handleSquareClick = (sq) => {
        if (apples.length === 0) return; 

        if (sq === piecePos) {
            setSelectedSquare(sq);
            return;
        }

        if (selectedSquare === piecePos) {
            if (isMoveLegal(piecePos, sq, chapter.piece)) {
                setPiecePos(sq);
                setSelectedSquare(null);
                setWrongAttempts(0); 

                if (apples.includes(sq)) {
                    playSound('capture'); 
                    const remaining = apples.filter(a => a !== sq);
                    setApples(remaining);
                    
                    if (remaining.length === 0) {
                        playSound('collect'); 
                        const isLastLevel = currentLevelIdx === chapter.levels.length - 1;
                        if (!isLastLevel) {
                            toast.success(`🎉 Level ${level.id} Cleared!`, { duration: 2000 });
                            setTimeout(() => setCurrentLevelIdx(currentLevelIdx + 1), 2000);
                        } else {
                            setTimeout(() => {
                                setShowWinModal(true);
                                if (onChapterComplete) onChapterComplete();
                                if (onWin) onWin();
                            }, 500);
                        }
                    }
                } else {
                    playSound('move'); 
                }
            } else {
                playSound('error'); 
                setSelectedSquare(null);
                setWrongAttempts(prev => prev + 1);
                setTotalChapterMistakes(prev => prev + 1);
                toast.error(`Oops! ${chapter.hint}`, { icon: '🚫' });
            }
        }
    };

    const legalMovesList = (selectedSquare === piecePos) ? calculateLegalMoves(piecePos, chapter.piece) : [];

    return (
        <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
            <div style={{width: '100%', maxWidth: 'min(90vw, 55vh)', textAlign: 'center' }}>
                {classroomId && (
                    <div style={{ padding: '6px 12px', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#f59e0b' }}>🎓 Assigned by Coach: {chapter.piece} Basics</span>
                    </div>
                )}
                <h2 style={{color: 'var(--text-primary)', margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800' }}>{chapter.piece} Training</h2>
                {wrongAttempts >= 2 && (
                    <p style={{color: '#10b981', margin: '0 0 5px 0', fontSize: '13px', fontStyle: 'italic', fontWeight: 'bold' }}>💡 Hint: Move to the glowing green squares!</p>
                )}
                <div className="glass-panel" style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px', padding: '8px 15px' }}>
                    <span style={{color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>Apples: {apples.length}</span>
                    <span style={{color: '#60a5fa', fontWeight: 'bold', fontSize: '14px' }}>Level {level.id} / {chapter.levels.length}</span>
                </div>

                {/* THE BOARD (RELATIVE CONTAINER) */}
                <div style={{position: 'relative', width: '100%', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                    <div style={{paddingBottom: '100%' }}></div>
                    <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                        
                        {/* DRAW SQUARES & COORDS */}
                        {ROWS.map((row, rIdx) => COLS.map((col, cIdx) => {
                            const sq = `${col}${row}`;
                            const isDark = (rIdx + cIdx) % 2 === 1;
                            const hasApple = apples.includes(sq);
                            const isHint = legalMovesList.includes(sq);
                            const { left, top } = getSquareCoords(sq);
                            const isSelectedSq = selectedSquare === sq;

                            return (
                                <div key={sq} onClick={() => handleSquareClick(sq)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        if (e.dataTransfer.getData('pieceToken') === 'hungry-piece') {
                                            handleSquareClick(sq);
                                        }
                                    }}
                                    style={{position: 'absolute', width: '12.5%', height: '12.5%',
                                        left, top, background: isSelectedSq ? 'rgba(96, 165, 250, 0.4)' : (isDark ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.05)'),
                                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                                        cursor: 'pointer'
                                    }}>
                                    {isHint && <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '30%', height: '30%', background: 'rgba(16, 185, 129, 0.8)', borderRadius: '50%', pointerEvents: 'none', zIndex: 1}} />}
                                    {hasApple && <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none', zIndex: 2 }}><span style={{fontSize: '24px', animation: 'bounce 2s infinite' }}>{APPLE_IMG}</span></div>}
                                    {cIdx === 0 && <span style={{position:'absolute', top:2, left:2, fontSize:'10px', fontWeight:'bold', color: 'rgba(255,255,255,0.3)', pointerEvents:'none'}}>{row}</span>}
                                    {rIdx === 7 && <span style={{position:'absolute', bottom:2, right:3, fontSize:'10px', fontWeight:'bold', color: 'rgba(255,255,255,0.3)', pointerEvents:'none'}}>{col}</span>}
                                </div>
                            );
                        }))}

                        {/* ABSOLUTE POSITIONED PIECE WITH CSS TRANSITION FOR SLIDING */}
                        <div 
                            onClick={() => handleSquareClick(piecePos)}
                            style={{position: 'absolute', width: '12.5%', height: '12.5%',
                                left: getSquareCoords(piecePos).left, 
                                top: getSquareCoords(piecePos).top,
                                transition: 'left 0.25s cubic-bezier(0.25, 1, 0.5, 1), top 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
                                zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <img 
                                src={PIECES[chapter.piece]} 
                                alt={chapter.piece} 
                                draggable={true}
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('pieceToken', 'hungry-piece');
                                    setSelectedSquare(piecePos);
                                }}
                                style={{width: '90%', height: '90%', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.5))', transform: selectedSquare === piecePos ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.1s' }} 
                            />
                        </div>
                    </div>
                </div>
                {/* 🎮 NAVIGATION CONTROLS */}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                    <button onClick={() => currentLevelIdx > 0 && setCurrentLevelIdx(currentLevelIdx - 1)} disabled={currentLevelIdx === 0} className="neu-btn" style={{opacity: currentLevelIdx === 0 ? 0.3 : 1 }}>
                        ⬅️ Prev
                    </button>
                    
                    <select 
                        value={chapter.id} 
                        disabled={!!classroomId}
                        onChange={(e) => {
                            const idx = HUNGRY_CHAPTERS.findIndex(c => c.id === e.target.value);
                            setCurrentChapterIdx(Math.max(0, idx));
                            setCurrentLevelIdx(0);
                            setTotalChapterMistakes(0);
                            setShowWinModal(false);
                        }} 
                        className="neu-input" 
                        style={{
                            padding: '12px', 
                            flex: 1, 
                            textAlign: 'center', 
                            cursor: classroomId ? 'not-allowed' : 'pointer',
                            opacity: classroomId ? 0.85 : 1
                        }}
                    >
                        {HUNGRY_CHAPTERS.map(c => (
                            <option key={c.id} value={c.id} style={{backgroundColor: 'var(--bg-primary)'}}>
                                {c.piece} Training Mode {classroomId && c.id === chapter.id ? ' (Assigned)' : ''}
                            </option>
                        ))}
                    </select>

                    <button onClick={() => currentLevelIdx < chapter.levels.length - 1 && setCurrentLevelIdx(currentLevelIdx + 1)} disabled={currentLevelIdx === chapter.levels.length - 1} className="neu-btn" style={{opacity: currentLevelIdx === chapter.levels.length - 1 ? 0.3 : 1 }}>
                        Next ➡️
                    </button>
                </div>

                {/* 🏆 WIN POPUP MODAL */}
                {showWinModal && (
                    <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'var(--bg-primary)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(15px)' }}>
                        <div className="glass-panel" style={{padding: '40px', textAlign: 'center', maxWidth: '400px', width: '90%', animation: 'floatUpModal 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
                            <div style={{fontSize: '70px', marginBottom: '10px', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))', animation: 'bounce 2s infinite' }}>🏆</div>
                            <h2 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '28px', fontWeight: '800' }}>Chapter Cleared!</h2>
                            <p style={{color: 'var(--text-secondary)', fontSize: '15px', marginBottom: '25px' }}>Performance Analysis</p>
                            
                            <div style={{display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '25px', fontSize: '50px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }}>
                                <span style={{animation: 'pulse 1s infinite' }}>⭐</span>
                                <span style={{opacity: totalChapterMistakes <= 3 ? 1 : 0.2, animation: totalChapterMistakes <= 3 ? 'pulse 1.2s infinite' : 'none', filter: totalChapterMistakes > 3 ? 'grayscale(100%)' : 'none' }}>⭐</span>
                                <span style={{opacity: totalChapterMistakes <= 1 ? 1 : 0.2, animation: totalChapterMistakes <= 1 ? 'pulse 1.4s infinite' : 'none', filter: totalChapterMistakes > 1 ? 'grayscale(100%)' : 'none' }}>⭐</span>
                            </div>
                            
                            <div className="neu-panel-inner" style={{padding: '15px', color: totalChapterMistakes === 0 ? '#10b981' : '#f59e0b', fontWeight: 'bold', fontSize: '16px', marginBottom: '30px' }}>
                                {totalChapterMistakes === 0 ? "Perfect Score! Flawless Execution! 🚀" : totalChapterMistakes <= 3 ? "Great Job! Mastered with minor mistakes. 👏" : "You did it! Keep practicing for 3 stars. 💪"}
                            </div>

                            <button onClick={() => {
                                setShowWinModal(false);
                                if (currentChapterIdx < HUNGRY_CHAPTERS.length - 1) {
                                    setCurrentChapterIdx(currentChapterIdx + 1);
                                    setCurrentLevelIdx(0);
                                    setTotalChapterMistakes(0);
                                }
                            }} className="neu-btn-primary" style={{width: '100%', fontSize: '16px' }}>
                                {currentChapterIdx < HUNGRY_CHAPTERS.length - 1 ? 'Start Next Chapter ➡️' : 'Close Gamemode ✖️'}
                            </button>
                        </div>

                        {/* ✨ CONFETTI BOMB */}
                        <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'hidden' }}>
                            {Array.from({ length: 50 }).map((_, i) => (
                                <div key={i} style={{ position: 'absolute', left: `${Math.random() * 100}%`, top: `-10%`, animation: `confettiFall ${2 + Math.random() * 3}s linear infinite`, animationDelay: `${Math.random() * 1.5}s`, fontSize: `${15 + Math.random() * 20}px`, opacity: 0.9, filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.5))' }}>
                                    {['🎉', '✨', '🍎', '⭐', '🎊', '🔥', '🏆'][Math.floor(Math.random() * 7)]}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <style>{`
                    @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
                    @keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.15); } 100% { transform: scale(1); } }
                    @keyframes floatUpModal { 0% { transform: scale(0.8) translateY(50px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
                    @keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(120vh) rotate(720deg); opacity: 0; } }
                `}</style>
            </div>
        </div>
    );
}
